import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import i18n from '../i18n'
import { SupportedLanguage, isRTL, languageNames, languageFlags } from '@tvia-ai/shared'

interface LanguageState {
  language: SupportedLanguage
  isRTL: boolean
  
  // Actions
  setLanguage: (language: SupportedLanguage) => void
  initializeLanguage: () => void
  getLanguageOptions: () => Array<{
    code: SupportedLanguage
    name: string
    flag: string
    isRTL: boolean
  }>
}

// Helper function to get initial language from storage (same as in i18n)
const getInitialLanguage = (): SupportedLanguage => {
  try {
    const storedLanguage = localStorage.getItem('language-storage')
    if (storedLanguage) {
      const parsed = JSON.parse(storedLanguage)
      if (parsed?.state?.language) {
        return parsed.state.language as SupportedLanguage
      }
    }
  } catch (e) {
    // Ignore parsing errors
  }
  
  // Fallback to browser language detection
  const browserLanguage = navigator.language.split('-')[0] as SupportedLanguage
  const supportedLanguages: SupportedLanguage[] = ['en', 'he', 'ar', 'de', 'fr']
  
  return supportedLanguages.includes(browserLanguage) ? browserLanguage : 'en'
}

export const useLanguageStore = create<LanguageState>()(
  persist(
    (set, get) => ({
      language: getInitialLanguage(),
      isRTL: isRTL(getInitialLanguage()),

      setLanguage: (language: SupportedLanguage) => {
        // Update i18n language
        i18n.changeLanguage(language)
        
        // Update document direction
        const isRTLLanguage = isRTL(language)
        document.documentElement.dir = isRTLLanguage ? 'rtl' : 'ltr'
        document.documentElement.lang = language
        
        // Update store
        set({
          language,
          isRTL: isRTLLanguage,
        })
        
        // Update page title direction class
        document.body.className = document.body.className.replace(
          /\b(ltr|rtl)\b/g, 
          isRTLLanguage ? 'rtl' : 'ltr'
        )
        
        if (!document.body.className.includes(isRTLLanguage ? 'rtl' : 'ltr')) {
          document.body.className += ` ${isRTLLanguage ? 'rtl' : 'ltr'}`
        }
      },

      initializeLanguage: () => {
        // This should only run once on app startup
        // Get the current i18n language (which was initialized from storage)
        const currentI18nLanguage = i18n.language as SupportedLanguage
        const currentStoreLanguage = get().language
        
        // If the store language differs from i18n language, sync them
        if (currentI18nLanguage && currentI18nLanguage !== currentStoreLanguage) {
          const supportedLanguages: SupportedLanguage[] = ['en', 'he', 'ar', 'de', 'fr']
          if (supportedLanguages.includes(currentI18nLanguage)) {
            // Update store to match i18n language without triggering setLanguage
            const isRTLLanguage = isRTL(currentI18nLanguage)
            document.documentElement.dir = isRTLLanguage ? 'rtl' : 'ltr'
            document.documentElement.lang = currentI18nLanguage
            
            // Update store state directly
            set({
              language: currentI18nLanguage,
              isRTL: isRTLLanguage,
            })
            
            // Update page title direction class
            document.body.className = document.body.className.replace(
              /\b(ltr|rtl)\b/g, 
              isRTLLanguage ? 'rtl' : 'ltr'
            )
            
            if (!document.body.className.includes(isRTLLanguage ? 'rtl' : 'ltr')) {
              document.body.className += ` ${isRTLLanguage ? 'rtl' : 'ltr'}`
            }
            return
          }
        }
        
        // If we're here, either languages match or i18n language is not supported
        // Apply the current store language to DOM
        const currentLanguage = get().language
        const isRTLLanguage = isRTL(currentLanguage)
        document.documentElement.dir = isRTLLanguage ? 'rtl' : 'ltr'
        document.documentElement.lang = currentLanguage
        
        // Update page title direction class
        document.body.className = document.body.className.replace(
          /\b(ltr|rtl)\b/g, 
          isRTLLanguage ? 'rtl' : 'ltr'
        )
        
        if (!document.body.className.includes(isRTLLanguage ? 'rtl' : 'ltr')) {
          document.body.className += ` ${isRTLLanguage ? 'rtl' : 'ltr'}`
        }
      },

      getLanguageOptions: () => {
        const supportedLanguages: SupportedLanguage[] = ['en', 'he', 'ar', 'de', 'fr']
        
        return supportedLanguages.map(code => ({
          code,
          name: languageNames[code],
          flag: languageFlags[code],
          isRTL: isRTL(code),
        }))
      },
    }),
    {
      name: 'language-storage',
      partialize: (state) => ({
        language: state.language,
      }),
    }
  )
) 